import React, { createContext, useContext, useState, useCallback } from 'react';

type Language = 'en' | 'mr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.courses': 'Courses',
    'nav.msCitAi': 'MS-CIT + AI',
    'nav.abacus': 'ABACUS',
    'nav.about': 'About',
    'nav.gallery': 'Gallery',
    'nav.contact': 'Contact',
    'nav.admissionOpen': 'Admission Open',
    
    // Hero
    'hero.title': 'CAREER POINT',
    'hero.subtitle': 'COMPUTER EDUCATION',
    'hero.headline': 'Upgrade Your Skills. Build Your Career.',
    'hero.subheading': 'Learn computer skills, technology and AI with practical training.',
    'hero.exploreCourses': 'Explore Courses',
    'hero.admissionEnquiry': 'Admission Enquiry',
    
    // Courses
    'courses.title': 'Our Courses',
    'courses.subtitle': 'Professional computer education for your bright future',
    'courses.learnMore': 'Learn More',
    'courses.duration': 'Duration',
    'courses.fees': 'Fees',
    
    // Course names
    'course.mscit': 'MS-CIT',
    'course.mscit.desc': 'Maharashtra State Certificate in Information Technology with AI integration',
    'course.dtp': 'DTP',
    'course.dtp.desc': 'Desktop Publishing with design tools and techniques',
    'course.java': 'Java Programming',
    'course.java.desc': 'Object-oriented programming with Java',
    'course.tally': 'Tally Prime with GST',
    'course.tally.desc': 'Complete accounting with GST compliance',
    'course.advTally': 'Advanced Tally',
    'course.advTally.desc': 'Advanced accounting and inventory management',
    'course.excel': 'Advanced Excel',
    'course.excel.desc': 'Data analysis and automation with Excel',
    'course.typing': 'Computer Typing',
    'course.typing.desc': 'Marathi, Hindi & English typing mastery',
    'course.c': 'C & C++',
    'course.c.desc': 'Foundation programming languages',
    'course.python': 'Python',
    'course.python.desc': 'Modern programming for AI and data science',
    'course.web': 'Web Designing',
    'course.web.desc': 'HTML, CSS, JavaScript and modern frameworks',
    'course.digital': 'Digital Marketing',
    'course.digital.desc': 'SEO, social media and online marketing',
    'course.abacus': 'ABACUS',
    'course.abacus.desc': 'Mental arithmetic and brain development',
    
    // MS-CIT + AI
    'ai.title': 'MS-CIT with AI',
    'ai.subtitle': 'Your Personal Study Partner',
    'ai.feature1': 'AI Tutor 24x7',
    'ai.feature1.desc': 'Get help anytime with our AI-powered tutor',
    'ai.feature2': 'Explain Tough Concepts',
    'ai.feature2.desc': 'Complex topics explained in simple language',
    'ai.feature3': 'Learn in Different Languages',
    'ai.feature3.desc': 'Study in your preferred language',
    'ai.feature4': 'AI-assisted Writing',
    'ai.feature4.desc': 'Improve your writing with AI suggestions',
    'ai.feature5': 'AI Design & Building',
    'ai.feature5.desc': 'Create designs with AI assistance',
    'ai.feature6': 'Step-by-step Explanations',
    'ai.feature6.desc': 'Detailed guidance for every concept',
    
    // Abacus
    'abacus.title': 'Career Point ABACUS Academy',
    'abacus.subtitle': 'Unlock Your Child\'s Potential',
    'abacus.feature1': 'Sharper Memory',
    'abacus.feature1.desc': 'Enhance memory power through mental calculation',
    'abacus.feature2': 'Faster Calculations',
    'abacus.feature2.desc': 'Lightning-fast arithmetic skills',
    'abacus.feature3': 'Better Focus',
    'abacus.feature3.desc': 'Improved concentration and attention span',
    'abacus.enroll': 'Enroll Now',
    
    // About
    'about.title': 'About Career Point',
    'about.subtitle': 'Building Future Professionals Since 2010',
    'about.desc': 'Career Point Computer Education is a leading computer training institute in Nagpur, providing quality education in IT and computer skills. Our experienced faculty and practical approach ensure students are job-ready.',
    'about.stat1': '5000+',
    'about.stat1.label': 'Students Trained',
    'about.stat2': '12+',
    'about.stat2.label': 'Years Experience',
    'about.stat3': '15+',
    'about.stat3.label': 'Expert Faculty',
    'about.stat4': '98%',
    'about.stat4.label': 'Success Rate',
    'about.feature1': 'Modern Computers',
    'about.feature2': 'Practical Training',
    'about.feature3': 'Experienced Teachers',
    'about.feature4': 'Clean Environment',
    'about.feature5': 'Internet Enabled',
    'about.feature6': 'Student Friendly',
    
    // Gallery
    'gallery.title': 'Student Gallery',
    'gallery.subtitle': 'Moments from our journey',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch for admissions and enquiries',
    'contact.address': 'Address',
    'contact.phone': 'Phone',
    'contact.website': 'Website',
    'contact.callNow': 'Call Now',
    'contact.whatsapp': 'WhatsApp',
    'contact.directions': 'Get Directions',
    'contact.enquiry': 'Admission Enquiry',
    
    // Form
    'form.title': 'Admission Enquiry',
    'form.name': 'Student Name',
    'form.mobile': 'Mobile Number',
    'form.course': 'Course Interested In',
    'form.batch': 'Preferred Batch',
    'form.message': 'Message',
    'form.submit': 'Submit Enquiry',
    'form.success': 'Thank you! We will contact you soon.',
    'form.selectCourse': 'Select a course',
    'form.selectBatch': 'Select batch timing',
    'form.batch.morning': 'Morning (8 AM - 10 AM)',
    'form.batch.afternoon': 'Afternoon (2 PM - 4 PM)',
    'form.batch.evening': 'Evening (6 PM - 8 PM)',
    
    // Footer
    'footer.tagline': 'Upgrade Your Skills. Build Your Career.',
    'footer.quickLinks': 'Quick Links',
    'footer.courses': 'Courses',
    'footer.contact': 'Contact',
    'footer.followUs': 'Follow Us',
    'footer.rights': 'All rights reserved.',
    
    // Language
    'lang.english': 'English',
    'lang.marathi': 'मराठी',
  },
  mr: {
    // Navigation
    'nav.home': 'मुख्यपृष्ठ',
    'nav.courses': 'कोर्सेस',
    'nav.msCitAi': 'MS-CIT + AI',
    'nav.abacus': 'अ‍ॅबॅकस',
    'nav.about': 'आमच्याबद्दल',
    'nav.gallery': 'गॅलरी',
    'nav.contact': 'संपर्क',
    'nav.admissionOpen': 'प्रवेश सुरू',
    
    // Hero
    'hero.title': 'कॅरियर पॉईंट',
    'hero.subtitle': 'कंप्युटर शिक्षण',
    'hero.headline': 'तुमचे कौशल्य सुधारा. तुमचे करिअर बनवा.',
    'hero.subheading': 'व्यावहारिक प्रशिक्षणासह संगणक कौशल्ये, तंत्रज्ञान आणि AI शिका.',
    'hero.exploreCourses': 'कोर्सेस पहा',
    'hero.admissionEnquiry': 'प्रवेश चौकशी',
    
    // Courses
    'courses.title': 'आमचे कोर्सेस',
    'courses.subtitle': 'तुमच्या उज्ज्वल भविष्यासाठी व्यावसायिक संगणक शिक्षण',
    'courses.learnMore': 'अधिक जाणून घ्या',
    'courses.duration': 'कालावधी',
    'courses.fees': 'फी',
    
    // Course names
    'course.mscit': 'MS-CIT',
    'course.mscit.desc': 'AI एकत्रीकरणासह महाराष्ट्र राज्य माहिती तंत्रज्ञान प्रमाणपत्र',
    'course.dtp': 'DTP',
    'course.dtp.desc': 'डिझाइन साधने आणि तंत्रांसह डेस्कटॉप प्रकाशन',
    'course.java': 'Java प्रोग्रामिंग',
    'course.java.desc': 'Java सह ऑब्जेक्ट-ओरिएंटेड प्रोग्रामिंग',
    'course.tally': 'Tally Prime with GST',
    'course.tally.desc': 'GST अनुपालनासह संपूर्ण लेखाकारी',
    'course.advTally': 'अ‍ॅडवान्स Tally',
    'course.advTally.desc': 'अ‍ॅडवान्स लेखाकारी आणि इन्व्हेंटरी व्यवस्थापन',
    'course.excel': 'अ‍ॅडवान्स Excel',
    'course.excel.desc': 'Excel सह डेटा विश्लेषण आणि ऑटोमेशन',
    'course.typing': 'कंप्युटर टायपिंग',
    'course.typing.desc': 'मराठी, हिंदी आणि इंग्रजी टायपिंग मास्टरी',
    'course.c': 'C & C++',
    'course.c.desc': 'फाउंडेशन प्रोग्रामिंग भाषा',
    'course.python': 'Python',
    'course.python.desc': 'AI आणि डेटा सायन्ससाठी आधुनिक प्रोग्रामिंग',
    'course.web': 'वेब डिझाइनिंग',
    'course.web.desc': 'HTML, CSS, JavaScript आणि आधुनिक फ्रेमवर्क्स',
    'course.digital': 'डिजिटल मार्केटिंग',
    'course.digital.desc': 'SEO, सोशल मीडिया आणि ऑनलाइन मार्केटिंग',
    'course.abacus': 'अ‍ॅबॅकस',
    'course.abacus.desc': 'मानसिक अंकगणित आणि मेंदू विकास',
    
    // MS-CIT + AI
    'ai.title': 'MS-CIT with AI',
    'ai.subtitle': 'तुमचा वैयक्तिक अभ्यास भागीदार',
    'ai.feature1': 'AI ट्यूटर 24x7',
    'ai.feature1.desc': 'आमच्या AI-पॉवर्ड ट्यूटरसह कधीही मदत मिळवा',
    'ai.feature2': 'कठीण संकल्पना स्पष्ट करा',
    'ai.feature2.desc': 'सोप्या भाषेत स्पष्टीकृत क्लिष्ट विषय',
    'ai.feature3': 'वेगवेगळ्या भाषांमध्ये शिका',
    'ai.feature3.desc': 'तुमच्या पसंतीच्या भाषेत अभ्यास करा',
    'ai.feature4': 'AI-सहाय्यित लेखन',
    'ai.feature4.desc': 'AI सूचनांसह तुमचे लेखन सुधारा',
    'ai.feature5': 'AI डिझाइन आणि बिल्डिंग',
    'ai.feature5.desc': 'AI सहाय्याने डिझाइन तयार करा',
    'ai.feature6': 'टप्प्याटप्प्याने स्पष्टीकरण',
    'ai.feature6.desc': 'प्रत्येक संकल्पनासाठी तपशीलवार मार्गदर्शन',
    
    // Abacus
    'abacus.title': 'कॅरियर पॉईंट अ‍ॅबॅकस अ‍ॅकॅडमी',
    'abacus.subtitle': 'तुमच्या मुलाची क्षमता अनलॉक करा',
    'abacus.feature1': 'तीक्ष्ण स्मरणशक्ती',
    'abacus.feature1.desc': 'मानसिक गणनाद्वारे स्मरणशक्ती वाढवा',
    'abacus.feature2': 'जलद गणना',
    'abacus.feature2.desc': 'वीजेच्या वेगाने अंकगणित कौशल्ये',
    'abacus.feature3': 'अधिक लक्ष',
    'abacus.feature3.desc': 'सुधारित एकाग्रता आणि लक्ष कालावधी',
    'abacus.enroll': 'आता नोंदणी करा',
    
    // About
    'about.title': 'कॅरियर पॉईंट बद्दल',
    'about.subtitle': '2010 पासून भविष्यातील व्यावसायिक तयार करत आहोत',
    'about.desc': 'कॅरियर पॉईंट कंप्युटर एज्युकेशन हे नागपूरमधील एक अग्रगण्य संगणक प्रशिक्षण संस्था आहे, जी IT आणि संगणक कौशल्यांमध्ये गुणवत्तापूर्ण शिक्षण प्रदान करते. आमचे अनुभवी फॅकल्टी आणि व्यावहारिक दृष्टिकोन विद्यार्थ्यांना नोकरी-तयार सुनिश्चित करतात.',
    'about.stat1': '5000+',
    'about.stat1.label': 'प्रशिक्षित विद्यार्थी',
    'about.stat2': '12+',
    'about.stat2.label': 'वर्षे अनुभव',
    'about.stat3': '15+',
    'about.stat3.label': 'तज्ञ फॅकल्टी',
    'about.stat4': '98%',
    'about.stat4.label': 'यशस्वी दर',
    'about.feature1': 'आधुनिक संगणक',
    'about.feature2': 'व्यावहारिक प्रशिक्षण',
    'about.feature3': 'अनुभवी शिक्षक',
    'about.feature4': 'स्वच्छ वातावरण',
    'about.feature5': 'इंटरनेट सक्षम',
    'about.feature6': 'विद्यार्थी अनुकूल',
    
    // Gallery
    'gallery.title': 'विद्यार्थी गॅलरी',
    'gallery.subtitle': 'आमच्या प्रवासातील क्षण',
    
    // Contact
    'contact.title': 'आमच्याशी संपर्क साधा',
    'contact.subtitle': 'प्रवेश आणि चौकशीसाठी संपर्क साधा',
    'contact.address': 'पत्ता',
    'contact.phone': 'फोन',
    'contact.website': 'वेबसाइट',
    'contact.callNow': 'आता कॉल करा',
    'contact.whatsapp': 'WhatsApp',
    'contact.directions': 'दिशा मिळवा',
    'contact.enquiry': 'प्रवेश चौकशी',
    
    // Form
    'form.title': 'प्रवेश चौकशी',
    'form.name': 'विद्यार्थ्याचे नाव',
    'form.mobile': 'मोबाईल नंबर',
    'form.course': 'आवडलेला कोर्स',
    'form.batch': 'पसंतीचा बॅच',
    'form.message': 'संदेश',
    'form.submit': 'चौकशी सबमिट करा',
    'form.success': 'धन्यवाद! आम्ही लवकरच तुमच्याशी संपर्क साधू.',
    'form.selectCourse': 'कोर्स निवडा',
    'form.selectBatch': 'बॅच वेळ निवडा',
    'form.batch.morning': 'सकाळ (सकाळी 8 - 10)',
    'form.batch.afternoon': 'दुपार (दुपारी 2 - 4)',
    'form.batch.evening': 'संध्याकाळ (संध्याकाळी 6 - 8)',
    
    // Footer
    'footer.tagline': 'तुमचे कौशल्य सुधारा. तुमचे करिअर बनवा.',
    'footer.quickLinks': 'द्रुत लिंक्स',
    'footer.courses': 'कोर्सेस',
    'footer.contact': 'संपर्क',
    'footer.followUs': 'आम्हाला फॉलो करा',
    'footer.rights': 'सर्व हक्क राखीव.',
    
    // Language
    'lang.english': 'English',
    'lang.marathi': 'मराठी',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t = useCallback((key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};